import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { plural } from '../../../Documents/format';
import { CHART, hasAnyFilter } from '../../constants';
import { agoLabel, fmtInt, fmtPct, fmtStamp, monthLabel, share } from '../../format';
import { journalApi } from '../../journalApi';
import { useLoad } from '../../hooks/useLoad';
import { InactivePharmacy, InactiveResponse, JournalFilters } from '../../types';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, Stat, StatStrip, SubText, TABLE_SX, TipBox, TooltipInjected, clip, tooltipPoint
} from './chartKit';

interface Props {
  filters: JournalFilters;
  paramsKey: string;
  active: boolean;
  reloadToken: number;
}

type Quick = 'all' | 'never' | 'lapsed' | 'stale';

const PAGE = 50;
/** «Давно не вносили» - последняя запись старше стольких дней */
const STALE_DAYS = 60;

type RegionPoint = InactiveResponse['byRegion'][number];

const RegionTip: React.FC<TooltipInjected<RegionPoint>> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  return (
    <TipBox
      title={p.region} subtitle={`${fmtInt(p.zone)} ${plural(p.zone, 'аптека', 'аптеки', 'аптек')} в зоне`}
      rows={[
        { label: 'Вносили по фильтрам', value: fmtInt(p.active), color: CHART.later, hint: fmtPct(share(p.active, p.zone)) },
        { label: 'Без записей', value: fmtInt(p.inactive), color: CHART.expired, bold: true, hint: fmtPct(share(p.inactive, p.zone)) }
      ]}
    />
  );
};

/** Статус аптеки без записей: никогда не вносила / вносила в прошлом месяце / давно не вносит */
function statusOf(p: InactivePharmacy, withPrev: boolean): { label: string; color: string; bg: string } {
  if (p.never) return { label: 'Не вносила ни разу', color: '#b91c1c', bg: 'rgba(220,38,38,0.08)' };
  if (withPrev && p.prevMonthCount) return { label: 'Вносила в прошлом месяце', color: '#b45309', bg: 'rgba(217,119,6,0.09)' };
  if (p.daysSinceLast !== null && p.daysSinceLast > STALE_DAYS) return { label: `Нет записей более ${STALE_DAYS} дн.`, color: '#475569', bg: 'rgba(100,116,139,0.1)' };
  return { label: 'Вносила ранее', color: '#0369a1', bg: 'rgba(2,132,199,0.08)' };
}

const RegionChart: React.FC<{ data: InactiveResponse }> = ({ data }) => {
  const points = data.byRegion.filter(r => r.zone > 0);
  return (
    <ChartCard title="По регионам" subtitle="Зелёный - аптеки, у которых есть записи по фильтрам, красный - без записей">
      {!points.length ? <NoData height={160} /> : (
        <Box sx={{ height: points.length * 26 + 36 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }} barCategoryGap={4}>
              <CartesianGrid stroke={CHART.grid} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} axisLine={{ stroke: BORDER }} tickLine={false} />
              <YAxis type="category" dataKey="region" width={150} tick={{ ...AXIS_TICK, fill: '#334155' }} axisLine={false} tickLine={false} interval={0} tickFormatter={v => clip(String(v), 22)} />
              <ChartTooltip content={<RegionTip />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
              <Bar dataKey="active" stackId="z" fill="#86efac" isAnimationActive={false} />
              <Bar dataKey="inactive" stackId="z" fill={CHART.expired} radius={[0, 3, 3, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </ChartCard>
  );
};

const InactiveTable: React.FC<{ data: InactiveResponse }> = ({ data }) => {
  const [picked, setQuick] = useState<Quick>('all');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);
  const withPrev = Boolean(data.previousMonth);
  // «Вносили в прошлом месяце» имеет смысл только при выбранном месяце
  const quick: Quick = picked === 'lapsed' && !withPrev ? 'all' : picked;
  const list = data.pharmacies;
  const counts: Record<Quick, number> = {
    all: list.length,
    never: list.filter(p => p.never).length,
    lapsed: withPrev ? list.filter(p => Boolean(p.prevMonthCount)).length : 0,
    stale: list.filter(p => !p.never && p.daysSinceLast !== null && p.daysSinceLast > STALE_DAYS).length
  };
  const quicks: Array<{ value: Quick; label: string }> = [
    { value: 'all', label: `Все · ${fmtInt(counts.all)}` },
    { value: 'never', label: `Ни разу · ${fmtInt(counts.never)}` },
    ...(withPrev ? [{ value: 'lapsed' as Quick, label: `Вносили в прошлом месяце · ${fmtInt(counts.lapsed)}` }] : []),
    { value: 'stale', label: `Более ${STALE_DAYS} дн. · ${fmtInt(counts.stale)}` }
  ];

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter(p => {
      if (quick === 'never' && !p.never) return false;
      if (quick === 'lapsed' && !p.prevMonthCount) return false;
      if (quick === 'stale' && (p.never || p.daysSinceLast === null || p.daysSinceLast <= STALE_DAYS)) return false;
      return !q || `${p.num} ${p.name} ${p.region}`.toLowerCase().indexOf(q) !== -1;
    });
  }, [list, quick, query]);

  return (
    <ChartCard
      wide title="Аптеки без записей"
      subtitle="Сначала - не вносившие ни разу, затем по давности последней записи. Последняя запись и число месяцев - за всё время, без учёта фильтров."
      actions={(
        <TextField
          size="small" placeholder="Номер, название, регион" value={query}
          onChange={e => { setQuery(e.target.value); setShown(PAGE); }}
          sx={{ width: 240, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
        />
      )}
    >
      <ToggleButtonGroup
        size="small" exclusive value={quick} onChange={(_, v: Quick | null) => { if (v) { setQuick(v); setShown(PAGE); } }}
        sx={{ mb: 1, flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1.1, py: 0.25, fontSize: '0.74rem', fontWeight: 600, textTransform: 'none' } }}
      >
        {quicks.map(q => <ToggleButton key={q.value} value={q.value}>{q.label}</ToggleButton>)}
      </ToggleButtonGroup>
      {!rows.length ? <NoData height={120} text={query ? 'Аптека не найдена' : 'Таких аптек нет'} /> : (
        <>
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 640 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell>Аптека</TableCell>
                  <TableCell>Регион</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Последняя запись</TableCell>
                  <TableCell align="right">Записей всего</TableCell>
                  <TableCell align="right">Месяцев с вводом</TableCell>
                  {withPrev && <TableCell align="right">{`В ${monthLabel(data.previousMonth).toLowerCase()}`}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, shown).map(p => {
                  const st = statusOf(p, withPrev);
                  return (
                    <TableRow key={p.guid}>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={p.name}>{p.name}</Typography>
                        <SubText>{p.num}</SubText>
                      </TableCell>
                      <TableCell>{p.region || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: st.color, bgcolor: st.bg }} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {p.lastRecord ? fmtStamp(p.lastRecord) : '-'}
                        {p.lastRecord && <SubText>{agoLabel(p.daysSinceLast)}</SubText>}
                      </TableCell>
                      <TableCell align="right">{p.totalRecords ? fmtInt(p.totalRecords) : '-'}</TableCell>
                      <TableCell align="right">{p.monthsWithRecords ? fmtInt(p.monthsWithRecords) : '-'}</TableCell>
                      {withPrev && (
                        <TableCell align="right" sx={{ color: p.prevMonthCount ? '#b45309 !important' : undefined }}>
                          {p.prevMonthCount ? `${fmtInt(p.prevMonthCount)} зап.` : '-'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length > shown && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setShown(s => s + PAGE)} sx={{ textTransform: 'none' }}>
                Показать ещё {Math.min(PAGE, rows.length - shown)} из {fmtInt(rows.length - shown)}
              </Button>
            </Box>
          )}
        </>
      )}
    </ChartCard>
  );
};

const InactiveView: React.FC<Props> = ({ filters, paramsKey, active, reloadToken }) => {
  const state = useLoad(
    active ? (silent: boolean, signal: AbortSignal) => journalApi.inactive(filters, silent, signal) : null,
    paramsKey, reloadToken, 'Не удалось найти аптеки без записей'
  );
  const data = state.data;

  const stats = useMemo<Stat[]>(() => {
    if (!data) return [];
    const t = data.totals;
    const never = data.pharmacies.filter(p => p.never).length;
    const out: Stat[] = [
      { label: 'Аптек в зоне', value: fmtInt(t.zone), tooltip: 'Аптеки, доступные вам, с учётом фильтров «Аптеки» и «Регион»' },
      { label: 'Вносили по фильтрам', value: fmtInt(t.active), color: '#15803d', hint: fmtPct(share(t.active, t.zone)) },
      { label: 'Без записей', value: fmtInt(t.inactive), color: t.inactive ? CHART.expired : undefined, hint: fmtPct(share(t.inactive, t.zone)) },
      { label: 'Не вносили ни разу', value: fmtInt(never), color: never ? CHART.expired : undefined, hint: 'за всё время журнала' }
    ];
    if (data.previousMonth) {
      const lapsed = data.pharmacies.filter(p => Boolean(p.prevMonthCount)).length;
      out.push({
        label: `Вносили в ${monthLabel(data.previousMonth).toLowerCase()}`, value: fmtInt(lapsed), color: lapsed ? '#b45309' : undefined,
        hint: 'а в этом месяце - нет', tooltip: 'Аптеки без записей в выбранном месяце, у которых были записи месяцем ранее по тем же фильтрам'
      });
    }
    return out;
  }, [data]);

  if (!data) {
    return (
      <ChartCard wide title="Аптеки без записей" loading={state.loading} error={state.error?.text} onRetry={state.retry}>
        {!state.error && <ChartSkeleton height={360} />}
      </ChartCard>
    );
  }

  const filtered = hasAnyFilter({ ...filters, pharmacies: [] });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: state.loading ? 0.65 : 1, transition: 'opacity 0.15s' }}>
      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Повторить</Button>}>
          {state.error.text}. На экране - данные прежнего запроса.
        </Alert>
      )}
      <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>
        {`Аптеки, у которых нет ни одной записи ${data.month ? `за ${monthLabel(data.month).toLowerCase()}` : 'за всё время'}`}
        {filtered ? ' с учётом выбранных фильтров (товар, срок, категория и др.)' : ''}.
      </Typography>
      <StatStrip items={stats} />
      <RegionChart data={data} />
      <InactiveTable data={data} />
    </Box>
  );
};

export default React.memo(InactiveView);
